import * as NextSkyTheme from "@NextSkyTheme/global";
import {
  eventModal,
  isWaitlistPost,
  hasWaitlistSubmit,
  markWaitlistSubmit,
  clearWaitlistSubmit,
} from "@NextSkyTheme/modal";

class WaitlistPopup extends HTMLElement {
  static triggerListenerAdded = false;

  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.trigger = this.dataset.trigger;
    this.errorMessage = this.dataset.errorMessage;
    this.invalidMessage = this.dataset.invalidMessage;
  }

  connectedCallback() {
    this.modal = this.querySelector("waitlist-modal-popup");
    this.dialog = this.querySelector('[role="dialog"]');
    this.form = this.querySelector("form.waitlist__form");
    this.input = this.form?.querySelector('input[type="email"]');
    this.button = this.form?.querySelector('button[type="submit"]');
    this.errorEl = this.querySelector("[data-waitlist-error]");

    this.form?.addEventListener("submit", this.onSubmit.bind(this), true);
    this.input?.addEventListener("invalid", this.onInvalid.bind(this));
    this.input?.addEventListener("input", this.hideError.bind(this));
    this.querySelectorAll("[data-waitlist-done]").forEach((button) =>
      button.addEventListener("click", this.close.bind(this))
    );
    window.addEventListener("pageshow", this.onPageShow.bind(this));

    // One document-level listener for every waitlist popup on the page.
    // Capture phase so it runs before smooth-anchor-scroll and theme handlers.
    if (!WaitlistPopup.triggerListenerAdded) {
      WaitlistPopup.triggerListenerAdded = true;
      document.addEventListener("click", WaitlistPopup.onDocumentClick, true);
    }

    if (window.Shopify && window.Shopify.designMode) {
      this.initDesignMode();
    }

    if (isWaitlistPost()) this.showPostResult();
  }

  static onDocumentClick(event) {
    const link = event.target.closest("a[href]");
    if (!link) return;
    const hash = new URL(link.href, window.location.href).hash;
    if (!hash) return;
    const popup = [...document.querySelectorAll("waitlist-popup")].find(
      (el) => el.trigger === hash
    );
    if (!popup) return;
    event.preventDefault();
    event.stopPropagation();
    popup.open();
  }

  open() {
    if (!this.modal || this.modal.classList.contains("active")) return;
    eventModal(this.modal, "open", false, null, true, false);
    NextSkyTheme.global.rootToFocus = this.modal;
  }

  close() {
    if (this.modal?.classList.contains("active")) {
      eventModal(this.modal, "close");
    }
  }

  onSubmit(event) {
    this.hideError();
    if (navigator.onLine === false) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showError(this.errorMessage);
      return;
    }
    markWaitlistSubmit(this.sectionId);
    this.setLoading(true);
  }

  onInvalid(event) {
    event.preventDefault();
    this.showError(this.invalidMessage);
    this.input.focus();
  }

  onPageShow(event) {
    if (!event.persisted) return;
    this.setLoading(false);
    if (!hasWaitlistSubmit()) return;
    clearWaitlistSubmit();
    this.showView("form");
    this.showError(this.errorMessage);
    this.open();
  }

  showPostResult() {
    const result = this.querySelector("[data-waitlist-result]")?.dataset
      .waitlistResult;
    clearWaitlistSubmit();
    window.history.replaceState(
      window.history.state,
      document.title,
      window.location.pathname
    );

    if (result === "success") {
      const heading = this.showView("success");
      this.open();
      heading?.focus({ preventScroll: true });
      return;
    }

    this.showError(
      result === "invalid" ? this.invalidMessage : this.errorMessage
    );
    this.open();
  }

  showView(name) {
    let heading = null;
    this.querySelectorAll("[data-waitlist-view]").forEach((view) => {
      view.hidden = view.dataset.waitlistView !== name;
      if (!view.hidden) heading = view.querySelector(".section-heading");
    });
    if (heading?.id) this.dialog?.setAttribute("aria-labelledby", heading.id);
    return heading;
  }

  setLoading(isLoading) {
    if (!this.button) return;
    this.button.classList.toggle("loading", isLoading);
    const loader = this.button.querySelector(".icon-loader");
    loader?.classList.toggle("opacity-0", !isLoading);
    loader?.classList.toggle("pointer-none", !isLoading);
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
    this.input?.setAttribute("aria-invalid", "true");
    this.input?.classList.add("error-input");
  }

  hideError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = "";
    this.errorEl.hidden = true;
    this.input?.removeAttribute("aria-invalid");
    this.input?.classList.remove("error-input");
  }

  initDesignMode() {
    document.addEventListener("shopify:section:select", (event) => {
      if (event.detail.sectionId === this.sectionId) this.open();
    });
    document.addEventListener("shopify:section:deselect", (event) => {
      if (event.detail.sectionId === this.sectionId) this.close();
    });
  }
}

if (!customElements.get("waitlist-popup")) {
  customElements.define("waitlist-popup", WaitlistPopup);
}
